import { useCallback, useState } from 'react';
import {
  Box,
  Divider,
  InputAdornment,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  styled,
  useTheme,
} from '@mui/material';
import { ReactComponent as UploadIcon } from '../../assets/icons/upload.svg';
import { ReactComponent as UploadedFileIcon } from '../../assets/icons/40/file_uploaded.svg';
import { getThemeColors } from '../../helpers/themeStyles';
import { ReactComponent as TrashIcon } from '../../assets/icons/24/remove.svg';
import InputField from '../../components/InputField';
import DateField from '../../components/DateField';
import Button from '../../components/Button';
import StatusBadge from '../../components/StatusBadge';
import SvgIcon from '../../components/SvgIcon';
import { formatDate } from '../../helpers/date';
import {
  PLATFORM_ICONS,
  PLATFORMS_WITH_APP_IDENTIFIER,
  SERVICE_OPTIONS,
} from '../../constants/service';
import { FILE_STATUS_VARIANTS } from '../../constants/file';
import { USER_STATUS_OPTIONS } from '../../constants/user';
import { getUserStatusLabel } from '../../helpers/status';
import { TOOLTIP_SLOT_PROPS } from '../../constants/tooltip';
import {
  ContentGrid,
  FormSection,
  LabelRow,
  ActivityToggle,
  AppsSection,
  AppsRow,
  DocumentsSection,
  SectionTitle,
  DocumentsGrid,
  DocumentCardWrapper,
  DocumentCard,
  DocumentCardActions,
  DocumentCardLabel,
  DocumentCardDateLabel,
  ActionIconButton,
} from './sharedStyles';

const StatusMenu = styled(Menu)({
  '& .MuiPaper-root': {
    background: '#303030',
    borderRadius: 14,
    marginTop: 4,
    border: '1px solid #494949',
  },
  '& .MuiList-root': {
    display: 'flex',
    flexDirection: 'column',
    padding: 16,
    gap: 12,
  },
});

const StatusMenuItem = styled(MenuItem)({
  padding: 0,
  '&:hover': {
    backgroundColor: 'transparent',
  },
});

const DriverDetailsTab = ({
  formData,
  setFormData,
  hasFormChanged,
  isSaving,
  verifiedServices,
  driverDocuments,
  drivioDocuments,
  onUploadClick,
  onFileClick,
  onRemove,
  onStatusClick,
  onSave,
  onOpenApplications,
  onServiceIdentifierClick,
  uploadingType,
}) => {
  const theme = useTheme();
  const colors = getThemeColors(theme);
  const [statusAnchorEl, setStatusAnchorEl] = useState(null);
  const statusMenuOpen = Boolean(statusAnchorEl);

  const handleStatusClick = useCallback((event) => {
    setStatusAnchorEl(event.currentTarget);
  }, []);

  const handleStatusMenuClose = useCallback(() => {
    setStatusAnchorEl(null);
  }, []);

  const handleStatusSelect = useCallback(
    (newStatus) => {
      setFormData((p) => ({ ...p, status: newStatus }));
      handleStatusMenuClose();
    },
    [setFormData, handleStatusMenuClose]
  );

  const documentCard = (doc) => (
    <DocumentCard
      sx={onFileClick ? { cursor: 'pointer' } : undefined}
      onClick={() => onFileClick?.(doc)}
    >
      <SvgIcon icon={UploadedFileIcon} color={colors.iconOnSurface} size={40} />
      <Tooltip arrow title={doc.name ?? ''} placement="bottom" slotProps={TOOLTIP_SLOT_PROPS}>
        <Box
          sx={{
            width: 'calc(100% - 24px)',
            minWidth: 0,
            overflow: 'hidden',
            textAlign: 'center',
          }}
        >
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {doc.name}
          </Typography>
        </Box>
      </Tooltip>
      <DocumentCardActions onClick={(e) => e.stopPropagation()}>
        <Tooltip arrow title="Supprimer" placement="bottom" slotProps={TOOLTIP_SLOT_PROPS}>
          <ActionIconButton $accentColor="#FF6B6B" onClick={() => onRemove(doc.id)}>
            <SvgIcon icon={TrashIcon} color="inherit" />
          </ActionIconButton>
        </Tooltip>
        <StatusBadge
          active
          variant={doc.status ?? FILE_STATUS_VARIANTS.PENDING}
          sx={{ cursor: 'pointer' }}
          onClick={(e) => onStatusClick(e, doc)}
        />
      </DocumentCardActions>
    </DocumentCard>
  );

  return (
    <ContentGrid>
      <FormSection>
        <InputField
          label="Nom"
          value={formData.name}
          onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
          fullWidth
          onClear={() => setFormData((p) => ({ ...p, name: '' }))}
        />

        <InputField
          label="E-mail"
          value={formData.email}
          onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
          fullWidth
          onClear={() => setFormData((p) => ({ ...p, email: '' }))}
        />

        <InputField
          label="Numéro de téléphone"
          value={formData.mobileNumber}
          onChange={(e) => setFormData((p) => ({ ...p, mobileNumber: e.target.value }))}
          fullWidth
          onClear={() => setFormData((p) => ({ ...p, mobileNumber: '' }))}
        />

        <InputField
          label="Ville"
          value={formData.city}
          onChange={(e) => setFormData((p) => ({ ...p, city: e.target.value }))}
          fullWidth
          onClear={() => setFormData((p) => ({ ...p, city: '' }))}
        />

        <DateField
          sx={{ width: '100%' }}
          label="Date de naissance"
          value={formData.birthDate || null}
          onChange={(date) =>
            setFormData((p) => ({ ...p, birthDate: date ? date.toISOString() : '' }))
          }
          onClear={() => setFormData((p) => ({ ...p, birthDate: '' }))}
          dateFormat="medium"
        />

        <InputField
          label="Pourcentage de contribution"
          value={formData.contributionPercentage}
          onChange={(e) => setFormData((p) => ({ ...p, contributionPercentage: e.target.value }))}
          type="number"
          fullWidth
          inputProps={{ min: 0, max: 100, step: 'any' }}
          sx={{
            '& .MuiOutlinedInput-input': {
              '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                WebkitAppearance: 'none',
                margin: 0,
              },
              MozAppearance: 'textfield',
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" sx={{ color: '#ACACAC' }}>
                <Typography sx={{ fontSize: 18, fontWeight: 500 }}>%</Typography>
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ mt: '14px' }}>
          <ActivityToggle>
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 0.06,
              }}
            >
              STATUT D'ACTIVITÉ
            </Typography>
            <StatusBadge
              active
              variant={formData.status}
              sx={{ cursor: 'pointer' }}
              onClick={handleStatusClick}
            >
              {getUserStatusLabel(formData.status)}
            </StatusBadge>
          </ActivityToggle>
          <StatusMenu
            anchorEl={statusAnchorEl}
            open={statusMenuOpen}
            onClose={(_, reason) => {
              if (reason === 'backdropClick' || reason === 'escapeKeyDown') handleStatusMenuClose();
            }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {USER_STATUS_OPTIONS.filter((status) => status !== formData.status).map((status) => (
              <StatusMenuItem key={status} onClick={() => handleStatusSelect(status)} disableRipple>
                <StatusBadge variant={status}>{getUserStatusLabel(status)}</StatusBadge>
              </StatusMenuItem>
            ))}
          </StatusMenu>
        </Box>

        <Divider sx={{ my: '14px' }} />

        <AppsSection>
          <LabelRow>
            <Typography sx={{ fontSize: 16, fontWeight: 500 }}>APPLIS RATTACHÉES</Typography>
            <Button
              variant="contained"
              sx={{ backgroundColor: colors.primaryActionBg, color: colors.primaryActionText }}
              onClick={onOpenApplications}
            >
              MODIFIER
            </Button>
          </LabelRow>
          <AppsRow>
            {verifiedServices.map((svc) => {
              const { platform } = svc;
              const PlatformIcon = PLATFORM_ICONS[platform];
              if (!PlatformIcon) return null;
              const canEditIdentifier = PLATFORMS_WITH_APP_IDENTIFIER.has(platform);
              const platformLabel = SERVICE_OPTIONS.find((o) => o.key === platform)?.label ?? '';
              const title = canEditIdentifier
                ? `Définir les identifiants ${platformLabel}`
                : platformLabel;
              return (
                <Tooltip
                  key={platform}
                  arrow
                  title={title}
                  placement="bottom"
                  slotProps={TOOLTIP_SLOT_PROPS}
                >
                  <Box
                    component={PlatformIcon}
                    onClick={canEditIdentifier ? () => onServiceIdentifierClick?.(svc) : undefined}
                    sx={{
                      width: 56,
                      height: 56,
                      flexShrink: 0,
                      cursor: canEditIdentifier ? 'pointer' : 'default',
                      ...(canEditIdentifier && {
                        '&:hover': { opacity: 0.85 },
                      }),
                    }}
                  />
                </Tooltip>
              );
            })}
          </AppsRow>
        </AppsSection>

        <Button
          variant="contained"
          color="primary"
          onClick={onSave}
          disabled={!hasFormChanged || !!isSaving}
          sx={{ mt: 3 }}
        >
          ENREGISTRER
        </Button>
      </FormSection>

      <Divider orientation="vertical" flexItem />

      <DocumentsSection>
        <SectionTitle>DOCUMENTS CHAUFFEUR</SectionTitle>
        <DocumentsGrid>
          {driverDocuments.map((doc) => (
            <DocumentCardWrapper key={doc.id}>
              {doc.empty ? (
                <Box
                  sx={{ cursor: 'pointer', opacity: uploadingType === doc.type ? 0.6 : 1 }}
                  onClick={() => onUploadClick(doc)}
                >
                  <UploadIcon />
                </Box>
              ) : (
                documentCard(doc)
              )}
              <Box>
                <DocumentCardLabel>{doc.label}</DocumentCardLabel>
                {doc.date && (
                  <DocumentCardDateLabel>
                    {formatDate(doc.date, { format: 'medium' })}
                  </DocumentCardDateLabel>
                )}
              </Box>
            </DocumentCardWrapper>
          ))}
        </DocumentsGrid>

        <Divider sx={{ my: 5 }} />

        <SectionTitle>DOCUMENTS DRIVIO</SectionTitle>
        <DocumentsGrid>
          {drivioDocuments.map((doc) => (
            <DocumentCardWrapper key={doc.id}>
              {doc.empty ? (
                <Box
                  sx={{ cursor: 'pointer', opacity: uploadingType === doc.type ? 0.6 : 1 }}
                  onClick={() => onUploadClick(doc)}
                >
                  <UploadIcon />
                </Box>
              ) : (
                documentCard(doc)
              )}
              <Box>
                <DocumentCardLabel>{doc.label}</DocumentCardLabel>
                {doc.date && (
                  <DocumentCardDateLabel>
                    {formatDate(doc.date, { format: 'medium' })}
                  </DocumentCardDateLabel>
                )}
              </Box>
            </DocumentCardWrapper>
          ))}
        </DocumentsGrid>
      </DocumentsSection>
    </ContentGrid>
  );
};

export default DriverDetailsTab;
